import { MapContainer, TileLayer, Marker, Popup, Tooltip, ZoomControl, useMap } from "react-leaflet";
import { LatLngBounds, LatLngExpression, Icon, DivIcon } from "leaflet";
import { ReactNode, useEffect } from "react";
import MarkerClusterGroup from "react-leaflet-cluster";
import "./MapWithMarkers.css";
import realizationPin from "../asset/realization-pin.png";

// Building type colors for filters and legend
const BUILDING_TYPE_COLORS: Record<string, string> = {
  "Scolaire": "#3B82F6",
  "Tertiaire": "#6B7280",
  "Logement": "#22C55E",
  "Loisirs": "#F97316",
  "Administration": "#8B5CF6",
  "Atelier": "#92400E",
  "Autre": "#EF4444",
};

export interface MapMarker {
  id: string;
  position: LatLngExpression;
  popup?: ReactNode;
  tooltip?: string;
  onClick?: () => void;
}

export interface MapWithMarkersProps {
  markers: MapMarker[];
  bounds?: LatLngBounds;
  center?: LatLngExpression;
  zoom?: number;
  height?: number | string;
  enableClustering?: boolean;
  onMarkerClick?: (markerId: string) => void;
}

const markerIcon = new Icon({
  iconUrl: realizationPin,
  iconSize: [50, 60],
  iconAnchor: [25, 60],
  popupAnchor: [0, -55],
});

// Cluster icon
const createClusterIcon = (cluster: any) => {
  const count = cluster.getChildCount();
  let dimension = 30;

  if (count > 50) {
    dimension = 50;
  } else if (count > 10) {
    dimension = 40;
  }

  const fontSize = dimension >= 50 ? "14px" : dimension >= 40 ? "12px" : "11px";

  return new DivIcon({
    html: `<div style="
      background: linear-gradient(135deg, #4F7942, #228B22);
      color: white;
      width: ${dimension}px;
      height: ${dimension}px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: bold;
      font-size: ${fontSize};
      border: 3px solid white;
      box-shadow: 0 3px 10px rgba(0,0,0,0.3);
    ">${count}</div>`,
    className: "marker-cluster",
    iconSize: [dimension, dimension],
  });
};

// Component to fit bounds when markers change
const FitBoundsOnLoad = ({ markers }: { markers: MapMarker[] }) => {
  const map = useMap();

  useEffect(() => {
    if (markers.length > 0) {
      const positions = markers.map(m => m.position as [number, number]);
      const bounds = new LatLngBounds(positions);
      map.fitBounds(bounds.pad(0.1));
    }
  }, [markers, map]);

  return null;
};

// Full screen control component
const FullscreenControl = () => {
  const map = useMap();

  useEffect(() => {
    const container = map.getContainer();

    const toggleFullscreen = () => {
      if (!document.fullscreenElement) {
        container.requestFullscreen?.();
      } else {
        document.exitFullscreen?.();
      }
    };

    const button = document.createElement("button");
    button.innerHTML = "⛶";
    button.title = "Plein écran";
    button.style.cssText = `
      position: absolute;
      top: 80px;
      right: 10px;
      z-index: 1000;
      width: 34px;
      height: 34px;
      background: white;
      border: 2px solid rgba(0,0,0,0.2);
      border-radius: 4px;
      cursor: pointer;
      font-size: 18px;
      display: flex;
      align-items: center;
      justify-content: center;
    `;
    button.onclick = toggleFullscreen;
    container.appendChild(button);

    return () => {
      container.removeChild(button);
    };
  }, [map]);

  return null;
};

export const MapWithMarkers = (props: MapWithMarkersProps) => {
  const {
    markers,
    bounds,
    center = [45.5, 5.5],
    zoom = 7,
    height = 500,
    enableClustering = true,
    onMarkerClick,
  } = props;

  const renderMarkers = () => {
    return markers.map((marker) => (
      <Marker
        key={marker.id}
        position={marker.position}
        icon={markerIcon}
        eventHandlers={{
          click: () => {
            marker.onClick?.();
            onMarkerClick?.(marker.id);
          },
        }}
      >
        {marker.tooltip && (
          <Tooltip
            direction="top"
            offset={[0, -55]}
            opacity={0.95}
            permanent={false}
          >
            <div style={{
              padding: "4px 8px",
              fontWeight: 500,
              maxWidth: 200,
            }}>
              {marker.tooltip}
            </div>
          </Tooltip>
        )}
        {marker.popup && (
          <Popup className="clean-popup">
            {marker.popup}
          </Popup>
        )}
      </Marker>
    ));
  };

  return (
    <MapContainer
      bounds={bounds?.pad(0.5)}
      center={!bounds ? center : undefined}
      zoom={!bounds ? zoom : undefined}
      scrollWheelZoom={true}
      zoomControl={false}
      style={{
        height: typeof height === "number" ? `${height}px` : height,
        width: "100%",
        borderRadius: "8px",
      }}
    >
      <TileLayer
        url="https://{s}.tile.openstreetmap.fr/osmfr/{z}/{x}/{y}.png"
        maxZoom={19}
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
      />
      <ZoomControl position="topright" />
      <FullscreenControl />
      {markers.length > 0 && !bounds && <FitBoundsOnLoad markers={markers} />}

      {enableClustering ? (
        <MarkerClusterGroup
          chunkedLoading
          iconCreateFunction={createClusterIcon}
          maxClusterRadius={60}
          spiderfyOnMaxZoom={true}
          showCoverageOnHover={false}
          disableClusteringAtZoom={16}
        >
          {renderMarkers()}
        </MarkerClusterGroup>
      ) : (
        renderMarkers()
      )}
    </MapContainer>
  );
};

export { BUILDING_TYPE_COLORS };
